import { useEffect, useMemo, useState } from "react";
import { Stethoscope, RefreshCw } from "lucide-react";
import { tipForDate, _tipsForTests as allTips } from "@/lib/dailyClinicalTip";

const CATEGORY_LABEL = {
  LOS: "Length of Stay",
  QUALITY: "Quality of Care",
  SAFETY: "Patient Safety",
  TJC: "Joint Commission",
  TEAM: "Treatment Team",
  CLINICAL: "Clinical Pearl",
};

/**
 * Clinical Tip of the Day card. Sits beside the motivational quote on the
 * dashboard header for the currently-selected day. Deterministic per date so
 * the whole team sees the same tip at morning huddle.
 *
 * The shuffle button PERSISTS the chosen tip on the day (via `onShuffle`
 * prop) so Word / Excel / Print exports render the exact tip the user
 * cycled to on-screen. If no persistent day exists yet (fresh date with no
 * census), the component falls back to a local-state shuffle so the user
 * can still preview different tips on-screen.
 */
export default function ClinicalTipCard({ dateIso, offset = 0, onShuffle }) {
  const [localOffset, setLocalOffset] = useState(0);

  useEffect(() => setLocalOffset(0), [dateIso]);

  const effectiveOffset = (offset || 0) + (onShuffle ? 0 : localOffset);

  const tip = useMemo(() => {
    if (!effectiveOffset) return tipForDate(dateIso);
    const base = tipForDate(dateIso);
    const baseIdx = allTips.findIndex((t) => t.text === base.text);
    return allTips[(baseIdx + effectiveOffset + allTips.length) % allTips.length];
  }, [dateIso, effectiveOffset]);

  const label = CATEGORY_LABEL[tip.category] || tip.category;

  const handleShuffle = () => {
    if (onShuffle) {
      onShuffle((offset || 0) + 1);
    } else {
      setLocalOffset((n) => n + 1);
    }
  };

  return (
    <div
      className="mt-4 flex items-start gap-3 rounded-xl border border-[#e2e8f0] bg-gradient-to-br from-[#f0f9ff] to-[#f8fafc] px-4 py-3 shadow-[0_1px_2px_rgba(0,0,0,0.02)]"
      data-testid="clinical-tip"
    >
      <Stethoscope className="w-4 h-4 mt-1 flex-shrink-0 text-[#d96c4e]" />
      <div className="flex-1 min-w-0">
        <p className="text-[10px] font-semibold tracking-[0.14em] uppercase text-[#d96c4e]">
          Clinical tip of the day · {label}
        </p>
        <p
          className="mt-1 font-heading text-[15px] leading-snug text-[#0f172a]"
          data-testid="clinical-tip-text"
        >
          {tip.text}
        </p>
      </div>
      <button
        type="button"
        onClick={handleShuffle}
        aria-label="Show another clinical tip"
        title={onShuffle ? "Show another clinical tip (saved for Word / Excel / Print)" : "Show another clinical tip (save the day to persist)"}
        data-testid="clinical-tip-shuffle"
        className="flex-shrink-0 p-1.5 rounded-md text-[#475569] hover:text-[#d96c4e] hover:bg-white/70 transition-colors"
      >
        <RefreshCw className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
